import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { PlaybackFile } from "../../types";

interface Props {
  date: Date;             // dia en visualizacion
  files: PlaybackFile[];
  cursorIso?: string;     // ISO datetime de la posicion actual
  onSeek?: (iso: string) => void;
  selection?: { start: string; end: string } | null;
  onSelectionChange?: (sel: { start: string; end: string } | null) => void;
  // Modo seleccion: cuando esta activo, click+drag normal selecciona un tramo
  // (en vez de hacer seek). Util para el flujo de descarga sin requerir Shift.
  selectMode?: boolean;
  // Callback al terminar una seleccion (mouse up tras drag); el padre puede
  // desactivar el modo seleccion automaticamente.
  onSelectModeEnd?: () => void;
  // Zoom: notifica el nivel (totalDia/ventana) o null cuando se ve el dia completo.
  // El padre muestra el indicador y el boton "Restablecer" fuera del header.
  onZoomChange?: (level: number | null) => void;
  // Incrementarlo vuelve al dia completo (boton "Restablecer" del padre).
  resetZoomNonce?: number;
}

function toLocalDahuaIso(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join("T");
}

function dayBounds(date: Date): { startMs: number; endMs: number } {
  const s = new Date(date);
  s.setHours(0, 0, 0, 0);
  const e = new Date(s);
  e.setDate(e.getDate() + 1);
  return { startMs: s.getTime(), endMs: e.getTime() };
}

// ---- Zoom de la barra ---------------------------------------------------------
// La barra muestra una VENTANA [viewS, viewE] del dia. Sin zoom (view === null) la
// ventana es el dia completo, exactamente como antes. Rueda del mouse sobre la barra
// = acercar/alejar anclado en la posicion del puntero (asi el instante bajo el mouse
// no se mueve); Shift+rueda (o rueda horizontal) o ARRASTRAR la barra con el boton
// izquierdo = desplazar la ventana; el padre ofrece "Restablecer" = volver al dia
// completo. Con la ventana chica, elegir un minuto (o un segundo) exacto con un
// clic es trivial.
const MIN_WINDOW_MS = 60_000;      // ventana minima: 1 minuto (zoom maximo 1440x)
const ZOOM_FACTOR = 1.5;           // por "clic" de rueda
const PAN_FRACTION = 0.15;         // desplazamiento por clic de rueda (fraccion de la ventana)
const DRAG_THRESHOLD_PX = 5;       // menos que esto entre presionar y soltar es un clic, no un arrastre
const FOLLOW_ANCHOR = 0.15;        // al seguir al cursor, dejarlo al 15% del borde izquierdo
// Paso de las etiquetas de hora segun la ventana visible: se elige el menor paso que
// de como maximo 24 marcas (las mismas 24 horas del dia completo).
const TICK_STEPS_MS = [
  60_000, 2 * 60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, 30 * 60_000,
  3_600_000, 2 * 3_600_000, 3 * 3_600_000,
];

function pickTickStep(windowMs: number, fullDay: boolean): number {
  // Dia completo: SIEMPRE una marca por hora, como antes (incluso el dia de 25 h
  // del cambio de horario, donde 25/1h > 24 elegiria pasos de 2 h).
  if (fullDay) return 3_600_000;
  for (const s of TICK_STEPS_MS) if (windowMs / s <= 24) return s;
  return TICK_STEPS_MS[TICK_STEPS_MS.length - 1];
}

function tickLabel(ms: number, stepMs: number): string {
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, "0");
  // Dia completo (paso >= 1h): solo la hora, como siempre. Con zoom: HH:MM.
  if (stepMs >= 3_600_000) return pad(d.getHours());
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Fusiona archivos contiguos del mismo tipo en bandas mas grandes. Esto evita
// que cientos de archivos cortos (1-2 min cada uno) se rendericen como 1-2px
// invisibles. Si el gap entre el fin de un archivo y el inicio del siguiente
// es menor a GAP_TOLERANCE_MS, se consideran continuos.
const GAP_TOLERANCE_MS = 60_000;

// Para que un "hueco sin grabacion" se renderice como banda gris, tiene que
// superar este umbral. Si es menor, lo absorbemos en el mergeBands de arriba
// para no llenar el timeline de slivers grises ininteligibles.
const GAP_MIN_VISIBLE_MS = 5 * 60_000;

// Calcula los intervalos del dia donde NO hay grabacion ("gaps") para
// renderizarlos con un fondo gris en el timeline. El operador ve a primer
// vistazo donde hay cobertura y donde no, en lugar de tener que comparar
// bandas verdes contra fondo negro.
function computeGapBands(
  files: PlaybackFile[],
  dayStartMs: number,
  dayEndMs: number,
): { start: number; end: number }[] {
  if (files.length === 0) return [{ start: dayStartMs, end: dayEndMs }];
  const sorted = [...files].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const gaps: { start: number; end: number }[] = [];
  // Bookend antes del primer archivo.
  const firstStart = new Date(sorted[0].start).getTime();
  if (firstStart - dayStartMs > GAP_MIN_VISIBLE_MS) {
    gaps.push({ start: dayStartMs, end: firstStart });
  }
  // Huecos entre archivos consecutivos. Usamos el "end maximo visto" hasta
  // ahora para tolerar archivos solapados (raro en Dahua pero defensivo).
  let prevEnd = new Date(sorted[0].end).getTime();
  for (let i = 1; i < sorted.length; i++) {
    const fs = new Date(sorted[i].start).getTime();
    const fe = new Date(sorted[i].end).getTime();
    if (fs - prevEnd > GAP_MIN_VISIBLE_MS) {
      gaps.push({ start: prevEnd, end: fs });
    }
    if (fe > prevEnd) prevEnd = fe;
  }
  // Bookend despues del ultimo archivo.
  if (dayEndMs - prevEnd > GAP_MIN_VISIBLE_MS) {
    gaps.push({ start: prevEnd, end: dayEndMs });
  }
  return gaps;
}

function mergeBands(files: PlaybackFile[]): { start: number; end: number; type: string }[] {
  if (files.length === 0) return [];
  const sorted = [...files].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const bands: { start: number; end: number; type: string }[] = [];
  let cur = {
    start: new Date(sorted[0].start).getTime(),
    end: new Date(sorted[0].end).getTime(),
    type: sorted[0].type,
  };
  for (let i = 1; i < sorted.length; i++) {
    const fs = new Date(sorted[i].start).getTime();
    const fe = new Date(sorted[i].end).getTime();
    if (fs - cur.end <= GAP_TOLERANCE_MS && sorted[i].type === cur.type) {
      cur.end = Math.max(cur.end, fe);
    } else {
      bands.push(cur);
      cur = { start: fs, end: fe, type: sorted[i].type };
    }
  }
  bands.push(cur);
  return bands;
}

export function Timeline({
  date, files, cursorIso, onSeek, selection, onSelectionChange,
  selectMode = false, onSelectModeEnd, onZoomChange, resetZoomNonce,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  // dragStart y hover en ms absolutos (no en fraccion del dia) para que sigan
  // valiendo cuando la ventana visible no es el dia completo.
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  // Boton izquierdo presionado sin Shift ni modo seleccion: clic o arrastre, se
  // decide al soltar. Si el puntero se movio menos de DRAG_THRESHOLD_PX fue un clic
  // y se hace seek al instante presionado (ms0); si se movio mas fue un arrastre que
  // desplazo la ventana con zoom (pan) y NO se hace seek. Sin zoom no hay nada que
  // desplazar: el arrastre no hace nada y solo el clic hace seek. x0/y0/vs0/vm/width
  // son el origen del gesto (puntero y ventana al presionar).
  const [pan, setPan] = useState<{
    x0: number; y0: number; ms0: number; vs0: number; vm: number; width: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  const panActiveRef = useRef(false);   // lo lee el listener de rueda (no se re-registra)
  const onSeekRef = useRef(onSeek);
  useEffect(() => { onSeekRef.current = onSeek; });

  const { startMs, endMs } = dayBounds(date);
  const totalMs = endMs - startMs;

  // Ventana visible. null = dia completo. Se resetea al cambiar de dia o cuando
  // el padre pide restablecer.
  const [view, setView] = useState<{ s: number; e: number } | null>(null);
  useEffect(() => { setView(null); setPan(null); }, [startMs]);
  useEffect(() => { if (resetZoomNonce) setView(null); }, [resetZoomNonce]);
  const viewS = view ? view.s : startMs;
  const viewE = view ? view.e : endMs;
  const viewMs = viewE - viewS;
  const zoomed = view != null;
  const zoomLevel = totalMs / viewMs;

  const onZoomChangeRef = useRef(onZoomChange);
  useEffect(() => { onZoomChangeRef.current = onZoomChange; });
  useEffect(() => {
    onZoomChangeRef.current?.(zoomed ? zoomLevel : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomed, viewS, viewE]);

  const clampMs = (ms: number) => Math.max(startMs, Math.min(endMs - 1000, ms));
  const msToIso = (ms: number) => toLocalDahuaIso(new Date(clampMs(ms)));

  // Pre-procesamos: archivos -> bandas fusionadas, luego cada banda -> % de la ventana.
  const bands = useMemo(() => {
    const merged = mergeBands(files);
    return merged.map((b, i) => {
      const a = Math.max(b.start, viewS);
      const z = Math.min(b.end, viewE);
      return { key: i, left: ((a - viewS) / viewMs) * 100, width: ((z - a) / viewMs) * 100, type: b.type };
    }).filter((b) => b.width > 0);
  }, [files, viewS, viewE, viewMs]);

  // Intervalos sin grabacion (gap bands en gris claro). Calculados igual que
  // mergeBands pero en negativo. Se renderizan DEBAJO de las bandas activas
  // (verde/rojo) — donde haya banda activa, ningun gap quedaria visible.
  const gapBands = useMemo(() => {
    const gaps = computeGapBands(files, startMs, endMs);
    return gaps.map((g, i) => {
      const a = Math.max(g.start, viewS);
      const z = Math.min(g.end, viewE);
      return {
        key: i, left: ((a - viewS) / viewMs) * 100, width: ((z - a) / viewMs) * 100,
        startMs: g.start, endMs: g.end,
      };
    }).filter((b) => b.width > 0);
  }, [files, startMs, endMs, viewS, viewE, viewMs]);

  // Marcas de tiempo (etiquetas del header y, con zoom, lineas del track) segun
  // la ventana visible. Con el dia completo son las 24 horas de siempre.
  const ticks = useMemo(() => {
    const step = pickTickStep(viewMs, !zoomed);
    const first = startMs + Math.floor((viewS - startMs) / step) * step;
    const out: { ms: number; left: number; width: number; label: string }[] = [];
    for (let t = first; t < viewE; t += step) {
      const a = Math.max(t, viewS);
      const z = Math.min(t + step, viewE);
      if (z <= a) continue;
      out.push({
        ms: t,
        left: ((a - viewS) / viewMs) * 100,
        width: ((z - a) / viewMs) * 100,
        label: tickLabel(t, step),
      });
    }
    return out;
  }, [viewS, viewE, viewMs, startMs, zoomed]);

  const cursorMs = cursorIso ? new Date(cursorIso).getTime() : NaN;
  const cursorInView = !isNaN(cursorMs) && cursorMs >= viewS && cursorMs <= viewE;
  // Sin zoom, como siempre: el cursor se clampa a los bordes del dia (un seek al
  // archivo que empieza el dia anterior se ve en 0%). Con zoom, fuera de la ventana
  // no se dibuja (el follow lo trae de vuelta cuando corresponde).
  const cursorPct = isNaN(cursorMs)
    ? null
    : zoomed
      ? (cursorInView ? ((cursorMs - viewS) / viewMs) * 100 : null)
      : Math.max(0, Math.min(100, ((cursorMs - viewS) / viewMs) * 100));
  // Mouse sobre la barra, arrastrando una seleccion o arrastrando la barra: el
  // usuario esta eligiendo un instante; la ventana NO debe moverse sola debajo
  // del puntero.
  const interacting = hover != null || dragStart != null || pan != null;

  // Seguir al cursor de reproduccion cuando SALE de la ventana con zoom por el
  // avance del video (estaba adentro y en el siguiente tick queda afuera): se
  // desplaza la ventana dejandolo al 15% del borde izquierdo. Si el usuario hizo
  // zoom/desplazo a otra zona (el cursor ya estaba afuera), NO se lo persigue:
  // esta eligiendo un instante ahi. Mientras interactua (hover/drag) se congela.
  const followRef = useRef<{ cursor: number; inView: boolean; viewS: number; viewE: number }>({
    cursor: NaN, inView: true, viewS: 0, viewE: 0,
  });
  useEffect(() => {
    const inView = !view || cursorInView;
    const prev = followRef.current;
    const viewChanged = prev.viewS !== viewS || prev.viewE !== viewE;
    if (viewChanged) {
      // Zoom/desplazamiento (del usuario o nuestro): re-anclar el estado.
      followRef.current = { cursor: cursorMs, inView, viewS, viewE };
      return;
    }
    if (interacting) return;
    const cursorMoved = !isNaN(cursorMs) && cursorMs !== prev.cursor;
    if (view && cursorMoved && prev.inView && !inView) {
      const vm = view.e - view.s;
      let ns = cursorMs - FOLLOW_ANCHOR * vm;
      ns = Math.max(startMs, Math.min(endMs - vm, ns));
      setView({ s: ns, e: ns + vm });
      followRef.current = { cursor: cursorMs, inView: true, viewS: ns, viewE: ns + vm };
      return;
    }
    followRef.current = { cursor: cursorMs, inView, viewS, viewE };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorMs, viewS, viewE, interacting]);

  // Rueda del mouse: zoom anclado en el puntero / desplazamiento. Listener manual
  // con passive:false para poder hacer preventDefault (React registra onWheel
  // como passive). Se lee la ventana actual desde un ref para no re-registrar el
  // listener en cada cambio de zoom.
  const viewRef = useRef({ viewS, viewE });
  viewRef.current = { viewS, viewE };
  useEffect(() => {
    const el = wrapRef.current;
    const track = ref.current;
    if (!el || !track) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (panActiveRef.current) return;   // arrastrando: la ventana la fija el arrastre
      const rect = track.getBoundingClientRect();
      if (rect.width === 0) return;
      const { viewS: vs, viewE: ve } = viewRef.current;
      const vm = ve - vs;
      const pan = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (pan) {
        if (vm >= totalMs) return;
        // Chrome convierte Shift+rueda en deltaX (deltaY=0); Firefox deja deltaY.
        // Tomamos el eje que traiga movimiento.
        const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
        if (delta === 0) return;
        const dir = delta > 0 ? 1 : -1;
        let ns = vs + dir * vm * PAN_FRACTION;
        ns = Math.max(startMs, Math.min(endMs - vm, ns));
        setView({ s: ns, e: ns + vm });
        return;
      }
      if (e.deltaY === 0) return;
      const factor = e.deltaY < 0 ? 1 / ZOOM_FACTOR : ZOOM_FACTOR;
      let nm = vm * factor;
      if (nm >= totalMs) { setView(null); return; }
      nm = Math.max(MIN_WINDOW_MS, nm);
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const anchor = vs + frac * vm;
      let ns = anchor - frac * nm;
      ns = Math.max(startMs, Math.min(endMs - nm, ns));
      setView({ s: ns, e: ns + nm });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [startMs, endMs, totalMs]);

  const xToMs = (clientX: number): number => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return viewS;
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    return viewS + (x / rect.width) * viewMs;
  };

  // Arrastre de la barra: listeners en window mientras dura el gesto, asi el puntero
  // puede salir de la barra (48 px de alto) sin cortarlo. Se limpian al soltar, si
  // el boton ya no esta presionado, al abrirse un menu contextual, si la ventana
  // del navegador pierde el foco, al cambiar de dia o al desmontar.
  useEffect(() => {
    if (!pan) return;
    const canPan = pan.vm < totalMs && pan.width > 0;   // sin zoom no hay nada que desplazar
    const track = ref.current;
    const onMove = (ev: MouseEvent) => {
      // El navegador puede tragarse el mouseup izquierdo (p. ej. clic derecho durante
      // el arrastre: el menu contextual nativo captura el mouse). Si llega un
      // movimiento sin el boton presionado, el gesto termino: se cancela sin seek.
      if ((ev.buttons & 1) === 0) { setPan(null); return; }
      const dx = ev.clientX - pan.x0;
      if (!panMovedRef.current) {
        if (Math.max(Math.abs(dx), Math.abs(ev.clientY - pan.y0)) < DRAG_THRESHOLD_PX) return;
        panMovedRef.current = true;
      }
      if (!canPan) return;
      // La ventana se desplaza lo que se movio el puntero (en ms): el instante que
      // quedo bajo el puntero al presionar viaja con el, clampeado al dia.
      let ns = pan.vs0 - dx * (pan.vm / pan.width);
      ns = Math.max(startMs, Math.min(endMs - pan.vm, ns));
      setView({ s: ns, e: ns + pan.vm });
      // Guia de hover coherente con la ventana nueva; fuera de la barra, sin guia.
      const rect = track?.getBoundingClientRect();
      const inside = rect != null && rect.width > 0
        && ev.clientX >= rect.left && ev.clientX <= rect.right
        && ev.clientY >= rect.top && ev.clientY <= rect.bottom;
      setHover(inside ? ns + ((ev.clientX - rect.left) / rect.width) * pan.vm : null);
    };
    const onUp = (ev: MouseEvent) => {
      if (ev.button !== 0) return;
      if (!panMovedRef.current) onSeekRef.current?.(msToIso(pan.ms0));
      setPan(null);
    };
    const onCancel = () => setPan(null);   // blur de la ventana o menu contextual: sin seek
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("blur", onCancel);
    window.addEventListener("contextmenu", onCancel);
    // Rueda ignorada durante cualquier gesto presionado (con o sin zoom): si se pudiera
    // hacer zoom a mitad de un clic sostenido, el gesto seguiria con la capacidad de
    // desplazar congelada al presionar y el puntero mentiria.
    panActiveRef.current = true;
    // Arrastrando con zoom: mano cerrada aunque el puntero salga de la barra, y sin
    // seleccion de texto por el camino. Se restaura lo que hubiera al terminar.
    const body = document.body;
    const prevCursor = body.style.cursor;
    const prevSelect = body.style.userSelect;
    if (canPan) { body.style.cursor = "grabbing"; body.style.userSelect = "none"; }
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("blur", onCancel);
      window.removeEventListener("contextmenu", onCancel);
      panActiveRef.current = false;
      if (canPan) { body.style.cursor = prevCursor; body.style.userSelect = prevSelect; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pan, startMs, endMs, totalMs]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const ms = xToMs(e.clientX);
    // selectMode (toggle UI) o Shift (atajo legacy) activan seleccion en lugar de seek.
    if (e.shiftKey || selectMode) {
      setDragStart(ms);
      onSelectionChange?.({ start: msToIso(ms), end: msToIso(ms) });
    } else {
      // Clic (seek) o arrastre (pan): se resuelve al soltar, en el efecto de `pan`.
      const rect = ref.current?.getBoundingClientRect();
      panMovedRef.current = false;
      setPan({
        x0: e.clientX, y0: e.clientY, ms0: ms, vs0: viewS, vm: viewMs, width: rect?.width ?? 0,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const ms = xToMs(e.clientX);
    setHover(ms);
    if (dragStart != null) {
      const a = Math.min(dragStart, ms);
      const b = Math.max(dragStart, ms);
      onSelectionChange?.({ start: msToIso(a), end: msToIso(b) });
    }
  };

  const handleMouseUp = () => {
    if (dragStart != null) {
      // Al terminar el drag, salir del modo seleccion para que el siguiente
      // click vuelva a hacer seek normal.
      onSelectModeEnd?.();
    }
    setDragStart(null);
  };

  // Tooltip de hover: muestra HH:MM:SS cuando el mouse esta sobre la timeline.
  // Si el cursor cae sobre un gap (intervalo sin grabacion), incluye "Sin grabacion".
  const hoverTime = useMemo(() => {
    if (hover == null) return null;
    return msToIso(hover).slice(11, 19);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover, startMs, endMs]);
  const hoverOverGap = useMemo(() => {
    if (hover == null) return false;
    return gapBands.some((g) => hover >= g.startMs && hover < g.endMs);
  }, [hover, gapBands]);
  const hoverPct = hover != null ? ((hover - viewS) / viewMs) * 100 : null;

  // Seleccion recortada a la ventana visible (puede quedar parcial o totalmente afuera).
  const sel = useMemo(() => {
    if (!selection) return null;
    const a = Math.max(new Date(selection.start).getTime(), viewS);
    const z = Math.min(new Date(selection.end).getTime(), viewE);
    if (isNaN(a) || isNaN(z) || z < a) return null;
    return { left: ((a - viewS) / viewMs) * 100, width: ((z - a) / viewMs) * 100 };
  }, [selection, viewS, viewE, viewMs]);

  return (
    <div ref={wrapRef} className="bg-panel border-t border-line">
      {/* Header con etiquetas de hora (marcas segun la ventana visible; dia completo = 24 horas) */}
      <div className="h-5 text-xs text-muted relative px-1 select-none">
        <div className="relative h-full overflow-hidden">
          {ticks.map((t) => (
            <div
              key={t.ms}
              className="absolute top-0 h-full border-r border-line text-center whitespace-nowrap overflow-hidden"
              style={{ left: `${t.left}%`, width: `${t.width}%` }}
            >
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* Track principal */}
      <div
        ref={ref}
        className={clsx(
          "relative h-12 select-none",
          selectMode ? "cursor-crosshair" : "cursor-pointer",
        )}
        // Con zoom la barra se puede arrastrar: mano abierta en reposo y cerrada
        // mientras se arrastra (inline: cursor-grab/grabbing no estan en el CSS compilado).
        // Durante una seleccion Shift+arrastre se mantiene el puntero de siempre.
        style={!selectMode && zoomed && dragStart == null ? { cursor: pan ? "grabbing" : "grab" } : undefined}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { setHover(null); setDragStart(null); }}
        onMouseUp={handleMouseUp}
      >
        {/* Lineas verticales de las marcas, solo con zoom (orientacion dentro de la ventana) */}
        {zoomed && (
          <div className="absolute inset-0 pointer-events-none">
            {ticks.map((t) => (
              <div
                key={t.ms}
                className="absolute top-0 h-full border-r border-line/50"
                style={{ left: `${t.left}%`, width: `${t.width}%` }}
              />
            ))}
          </div>
        )}

        {/* Bandas grises de "sin grabacion": fondo del track para zonas donde
            no hay archivos. Se renderizan PRIMERO para que las bandas activas
            verdes/rojas las cubran si hay solapamiento accidental. */}
        {gapBands.map((g) => (
          <div
            key={`gap-${g.key}`}
            className="absolute top-2 h-8 bg-line/40"
            style={{ left: `${g.left}%`, width: `${g.width}%` }}
            title="Sin grabacion"
          />
        ))}

        {/* Bandas de grabacion (verde = regular, rojo = alarma).
            minWidth: 3px asegura visibilidad aun para tramos cortos. */}
        {bands.map((b) => (
          <div
            key={b.key}
            className={clsx(
              "absolute top-2 h-8 rounded-sm",
              b.type === "alarm" ? "bg-red-700/70" : "bg-green-700/70",
            )}
            style={{
              left: `${b.left}%`,
              width: `${b.width}%`,
              minWidth: "3px",
            }}
          />
        ))}

        {/* Seleccion activa (Shift+drag o selectMode) */}
        {sel && (
          <div
            className="absolute top-0 bottom-0 bg-accent/30 border-x-2 border-accent pointer-events-none"
            style={{ left: `${sel.left}%`, width: `${sel.width}%` }}
          />
        )}

        {/* Cursor de reproduccion (solo si cae dentro de la ventana visible) */}
        {cursorPct != null && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-accent pointer-events-none"
            style={{ left: `${cursorPct}%` }}
          />
        )}

        {/* Hover guide */}
        {hoverPct != null && (
          <>
            <div
              className="absolute top-0 bottom-0 w-px bg-white/30 pointer-events-none"
              style={{ left: `${hoverPct}%` }}
            />
            <div
              className="absolute -top-5 text-[10px] text-white bg-black/70 px-1 rounded pointer-events-none whitespace-nowrap"
              style={{ left: `${hoverPct}%`, transform: "translateX(-50%)" }}
            >
              {hoverTime}{hoverOverGap ? " — sin grabacion" : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
